import Link from "next/link";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Heading } from "@otrium/atoms";
import { ChevronTextSmallLeft } from "src/icons/ChevronTextSmallLeft";
import { useCategoryLevelView } from "./use-category-level-view";
import {
  CategoryPill,
  CategoryPillsBack,
  CategoryPillsContainer,
  CategoryPillsHeader,
  CategoryPillsNav,
} from "./CategoryPills.styled";
import type { Gender, ProductListType } from "src/types/product-listing";

type CategoryPillsProps = {
  gender?: Gender;
  categorySlug?: string;
  pageContentType?: ProductListType;
  isPageTitle?: boolean;
  handleScrollToTop?: () => void;
};

function CategoryPills({
  gender,
  categorySlug,
  pageContentType,
  isPageTitle = false,
  handleScrollToTop,
}: CategoryPillsProps): JSX.Element | null {
  const { i18n } = useLingui();
  const { view, isReady, drillsByFilter } = useCategoryLevelView({
    gender,
    categorySlug,
    pageContentType,
  });

  if (!isReady || (!view.items.length && !view.current)) {
    return null;
  }

  const scroll = !drillsByFilter;
  const onNavigate = drillsByFilter ? handleScrollToTop : undefined;
  const label = view.back?.label ?? "";
  const rowLabel = isPageTitle || !view.isLeaf ? view.current?.label : label;

  return (
    <CategoryPillsContainer>
      {view.current ? (
        <CategoryPillsHeader>
          {view.back ? (
            <Link
              href={view.back.href}
              prefetch={false}
              scroll={scroll}
              passHref
              legacyBehavior
            >
              <CategoryPillsBack
                aria-label={t(i18n)`Back to ${label}`}
                onClick={onNavigate}
              >
                <ChevronTextSmallLeft />
                <Heading as="h4" variant="h4">
                  {rowLabel}
                </Heading>
              </CategoryPillsBack>
            </Link>
          ) : (
            <Heading as="h4" variant="h4">
              {view.current.label}
            </Heading>
          )}
        </CategoryPillsHeader>
      ) : null}

      {view.items.length ? (
        <CategoryPillsNav aria-label="Categories">
          {view.items.map((item) => (
            <Link
              key={item.itemId}
              href={item.href}
              prefetch={false}
              scroll={scroll}
              passHref
              legacyBehavior
            >
              <CategoryPill
                isActive={item.isActive}
                aria-current={item.isActive ? "page" : undefined}
                onClick={onNavigate}
              >
                {item.label}
              </CategoryPill>
            </Link>
          ))}
        </CategoryPillsNav>
      ) : null}
    </CategoryPillsContainer>
  );
}

export { CategoryPills };
